import {Grid, makeStyles, Typography, useMediaQuery} from '@material-ui/core';
import {CommissionAgentDto} from 'Agent/Accounts/api';
import {AccountDetail} from 'Agent/Accounts/models/AccountDetail';
import React, {ReactNode} from 'react';
import {AllstateCardV2} from 'Shared/components/AllstateCardV2';
import {CardFieldV2} from 'Shared/components/CardFieldV2';
import {SwipeableView} from 'Shared/components/SwipeableView';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import { maskPhoneNumber } from 'Shared/utilities/phoneNumberMasker';

const useAccountInformationSidebarStyles = makeStyles(theme => ({
  accountInformationSidebar: {
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '1.15rem',
    },
    '&::-webkit-scrollbar-track': {
      background: '#E5E5E5',
      borderRadius: '0.35rem',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#A6A5A5',
      border: '5px solid transparent',
      borderRadius: '8rem',
      backgroundClip: 'content-box',
    },
    margin: '0.25rem .25rem',
    [theme.breakpoints.up('md')]: {
      maxHeight: '85rem',
    },
  },
}));

const useAccountDetailInformationSectionStyles = makeStyles(_theme => ({
  heading: {
    color: '#0E1738',
    width: '100%',
    fontSize: '1rem',
    fontWeight: 600,
    textAlign: 'left',
    padding: '0 1.75rem',
    marignTop: '1.5rem',
    marginBottom: '-0.25rem',
  },
  gridContainer: {
    padding: '1rem',
    width: '100%',
    margin: 0,
    justifyContent: 'flex-start',
  },
  separator: {
    border: '1.5px solid #ccc',
    width: '95%',
    '&:last-of-type': {
      display: 'none',
    },
  },
  stepper: {
    backgroundColor: 'transparent',
    paddingBottom: '1rem',
  },
}));

type AccountDetailInformationSectionProps = {
  title: string;
  children?: ReactNode;
  isMobile?: boolean;
};

const AccountDetailInformationSection = ({title, children, isMobile}: AccountDetailInformationSectionProps) => {
  const {gridContainer, separator, heading} = useAccountDetailInformationSectionStyles();
  return (
    <>
      <div>
        {!isMobile && <h3 className={heading}>{title}</h3>}
        <Grid container spacing={3} className={gridContainer}>
          {children}
        </Grid>
      </div>
      {!isMobile && <hr className={separator} />}
    </>
  );
};

const AccountDetailSet1 = ({accountDetail}: AccountDetailProps) => (
  <>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Account Name" value={accountDetail?.name} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Account Number" value={accountDetail?.number} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Group Number" value={accountDetail?.groupNumber} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Contact Name" value={accountDetail?.contact} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Contact Number" value={maskPhoneNumber(accountDetail?.phoneNumber)} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Annualized Premium" value={toMoneyValue(accountDetail?.annualizedPremium)} />
    </Grid>
  </>
);

const AccountDetailSet2 = ({accountDetail}: AccountDetailProps) => (
  <>
    {!Boolean(accountDetail?.eisFlag) && (
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="Account Team" value={accountDetail?.accountTeam} />
      </Grid>
    )}
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Effective Date" value={toFormattedDateAsUTC(accountDetail?.accountEffectiveDate)} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="E-Delivery" value={accountDetail?.eDelivery ? 'Yes' : 'No'} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Agent Of Record" value={accountDetail?.agentOfRecordNumber} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Servicing Agents" value={accountDetail?.servicingAgents?.join(', ')} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Last App Submitted Date" value={toFormattedDateAsUTC(accountDetail?.lastAppSubmittedDate)} />
    </Grid>
  </>
);

const AccountDetailSet3 = ({accountDetail}: AccountDetailProps) => (
  <>
    {!accountDetail?.eisFlag && (
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="Termination Date" value={toFormattedDateAsUTC(accountDetail?.accountTerminationDate)} />
      </Grid>
    )}
    {!accountDetail?.eisFlag && (
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="Termination Reason" value={accountDetail?.terminationCodeDesc} />
      </Grid>
    )}
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Account Status" value={accountDetail?.status} />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Source" value={accountDetail?.source?.name} />
    </Grid>
  </>
);

const AccountDetails = ({accountDetail}: AccountDetailProps) => {
  const styles = useAccountDetailInformationSectionStyles();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return isMobile ? (
    <SwipeableView styles={{stepper: styles.stepper}}>
      <AccountDetailInformationSection title="Account Detail" isMobile={isMobile}>
        <AccountDetailSet1 accountDetail={accountDetail} />
      </AccountDetailInformationSection>
      <AccountDetailInformationSection title="Account Detail" isMobile={isMobile}>
        <AccountDetailSet2 accountDetail={accountDetail} />
      </AccountDetailInformationSection>
      <AccountDetailInformationSection title="Account Detail" isMobile={isMobile}>
        <AccountDetailSet3 accountDetail={accountDetail} />
      </AccountDetailInformationSection>
    </SwipeableView>
  ) : (
    <AccountDetailInformationSection title="Account Detail" isMobile={isMobile}>
      <AccountDetailSet1 accountDetail={accountDetail} />
      <AccountDetailSet2 accountDetail={accountDetail} />
      <AccountDetailSet3 accountDetail={accountDetail} />
    </AccountDetailInformationSection>
  );
};

const AccountAddress = ({accountAddress}: {accountAddress: AccountDetail['address']}) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return accountAddress ? (
    <AccountDetailInformationSection title="Address" isMobile={isMobile}>
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="Full Address" value={accountAddress.fullAddress} ddprivacy="mask" />
      </Grid>
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="State" value={accountAddress.state} ddprivacy="mask" />
      </Grid>
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="City" value={accountAddress.city} ddprivacy="mask" />
      </Grid>
      <Grid item xs={12} sm={6} md={12}>
        <CardFieldV2 name="Zip Code" value={accountAddress.zipCode} ddprivacy="mask" />
      </Grid>
    </AccountDetailInformationSection>
  ) : null;
};

const CommissionAgent = ({agent}: {agent: CommissionAgentDto}) => (
  <>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Name" value={agent.name} ddprivacy="mask" />
    </Grid>
    <Grid item xs={12} sm={6} md={12}>
      <CardFieldV2 name="Number" value={agent.number} ddprivacy="mask" />
    </Grid>
  </>
);

const CommissionAgents = ({commissionAgents}: {commissionAgents: CommissionAgentDto[]}) => {
  const styles = useAccountDetailInformationSectionStyles();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <>
      {isMobile ? (
        <SwipeableView styles={{stepper: styles.stepper}}>
          {commissionAgents.map((agent, index) => (
            <AccountDetailInformationSection title="Commission Agents" key={`agent-${index}-${agent.number}`}>
              <CommissionAgent agent={agent} />
            </AccountDetailInformationSection>
          ))}
        </SwipeableView>
      ) : (
        <>
          {Array.isArray(commissionAgents) && commissionAgents.length > 0 && (
            <AccountDetailInformationSection title="Commission Agents">
              {commissionAgents.map((agent, index) => (
                <React.Fragment key={`agent-${index}-${agent.number}`}>
                  <CommissionAgent agent={agent} />
                  <hr className={styles.separator} style={{width: '100%'}} />
                </React.Fragment>
              ))}
            </AccountDetailInformationSection>
          )}
        </>
      )}
    </>
  );
};

type AccountDetailProps = {
  accountDetail: AccountDetail;
};

const AccountDetailSection = ({accountDetail}: AccountDetailProps) => {
  const classes = useAccountInformationSidebarStyles();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <>
      {isMobile ? (
        <>
          <AllstateCardV2 title="Account Information">
            <AccountDetails accountDetail={accountDetail} />
          </AllstateCardV2>
          {accountDetail?.address && (
            <AllstateCardV2 title="Address">
              <AccountAddress accountAddress={accountDetail?.address} />
            </AllstateCardV2>
          )}
          {Array.isArray(accountDetail?.commissionAgents) && accountDetail?.commissionAgents.length > 0 && (
            <AllstateCardV2 title="Commission Agents">
              <CommissionAgents commissionAgents={accountDetail?.commissionAgents} />
            </AllstateCardV2>
          )}
        </>
      ) : (
        <AllstateCardV2 title="Account Information" className={classes.accountInformationSidebar}>
          <Typography component="div" tabIndex={0}>
            <AccountDetails accountDetail={accountDetail} />
            {accountDetail?.address && <AccountAddress accountAddress={accountDetail?.address} />}
            {Array.isArray(accountDetail?.commissionAgents) && accountDetail?.commissionAgents.length > 0 && (
              <CommissionAgents commissionAgents={accountDetail?.commissionAgents} />
            )}
          </Typography>
        </AllstateCardV2>
      )}
    </>
  );
};

export default AccountDetailSection;